import { ChevronDown, Search, X } from 'lucide-react';
import {
  createContext,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
  useId,
  useState,
} from 'react';
import { useT } from '@/i18n';
import type { Currency } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { amountInArabicWords } from '@/lib/format/arabic-words';
import { currencySymbol, hasSeparator, parseMoneyInput } from '@/lib/format/money';
import { useFormat } from '@/lib/format/useFormat';

// ------------------------------------------------------------------ Field

interface FieldCtx {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required?: boolean;
}

const FieldContext = createContext<FieldCtx | null>(null);

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  optional?: boolean;
  className?: string;
  /** Visually hide the label (still read by screen readers). */
  hideLabel?: boolean;
  children: ReactNode;
}

/** Label + control + hint/error, wired with ids so screen readers announce them. */
export function Field({ label, hint, error, required, optional, className, hideLabel, children }: FieldProps) {
  const t = useT();
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: !!error, required }}>
      <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
        <label htmlFor={id} className={cn('text-sm font-medium text-muted', hideLabel && 'sr-only')}>
          {label}
          {optional ? <span className="ms-1 font-normal text-subtle">({t('common.optional')})</span> : null}
        </label>
        {children}
        {error ? (
          <p id={errorId} className="text-sm text-danger-text">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-sm text-subtle">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

function useFieldProps(props: { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: unknown; required?: boolean }) {
  const ctx = useContext(FieldContext);
  return {
    id: props.id ?? ctx?.id,
    'aria-describedby': props['aria-describedby'] ?? ctx?.describedBy,
    'aria-invalid': (props['aria-invalid'] as boolean | undefined) ?? (ctx?.invalid || undefined),
    required: props.required ?? ctx?.required,
  };
}

export const controlClass = cn(
  'w-full min-w-0 rounded-md border border-field-line bg-field text-base text-ink placeholder:text-subtle',
  'transition-colors hover:border-muted focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0',
  'disabled:cursor-not-allowed disabled:bg-track disabled:text-subtle',
  'aria-invalid:border-danger aria-invalid:focus-visible:outline-danger',
);

// ------------------------------------------------------------------ Input

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
}

export function Input({ className, ...props }: InputProps) {
  const field = useFieldProps(props);
  return <input {...props} {...field} className={cn(controlClass, 'h-11 px-3', className)} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const field = useFieldProps(props);
  return <textarea rows={3} {...props} {...field} className={cn(controlClass, 'min-h-20 px-3 py-2.5 leading-relaxed', className)} />;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  ref?: Ref<HTMLSelectElement>;
}

/** Native select (keyboard, screen readers and mobile pickers for free), styled. */
export function Select({ className, children, placeholder, ...props }: SelectProps) {
  const field = useFieldProps(props);
  return (
    <div className={cn('relative', className)}>
      <select {...props} {...field} className={cn(controlClass, 'h-11 appearance-none ps-3 pe-9')}>
        {placeholder !== undefined ? (
          <option value="" disabled={props.required}>
            {placeholder}
          </option>
        ) : null}
        {children}
      </select>
      <ChevronDown aria-hidden className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-subtle" />
    </div>
  );
}

// ------------------------------------------------------------------ Date

export interface DateSelectProps {
  /** YYYY-MM-DD, or '' for no date. */
  value: string;
  /** A full date (kept within min/max), or '' when the person clears it (clearable only). */
  onValueChange: (value: string) => void;
  min?: string;
  max?: string;
  /** Years offered around today, where min/max do not set them. */
  yearsBack?: number;
  yearsAhead?: number;
  /** The date may be left empty (optional fields, filters). */
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

type DateParts = { d: string; m: string; y: string };

const splitDate = (iso: string): DateParts =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) ? { y: iso.slice(0, 4), m: String(Number(iso.slice(5, 7))), d: String(Number(iso.slice(8, 10))) } : { d: '', m: '', y: '' };
const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * A date as three native selects, day · month · year, in the school's language,
 * month names (تشرين الأول in Iraq, أكتوبر in Libya) and digits, instead of the
 * browser's date box, which follows the device's settings and differs per browser.
 * It reports a date once all three parts are chosen; picking the empty choice of
 * any part clears it (when clearable).
 */
export function DateSelect({ value, onValueChange, min, max, yearsBack = 5, yearsAhead = 2, clearable, disabled, className, ...rest }: DateSelectProps) {
  const t = useT();
  const fmt = useFormat();
  const ctx = useContext(FieldContext);
  // A part-chosen date (e.g. only the month) until all three parts are set; it
  // belongs to the value it was started from, so a new value from outside replaces it.
  const [draft, setDraftFor] = useState<{ from: string; parts: DateParts } | null>(null);
  const parts = draft && draft.from === value ? draft.parts : splitDate(value);
  const setDraft = (p: DateParts | null) => setDraftFor(p && { from: value, parts: p });

  const thisYear = Number(fmt.today().slice(0, 4));
  const first = min ? Number(min.slice(0, 4)) : thisYear - yearsBack;
  const last = max ? Number(max.slice(0, 4)) : thisYear + yearsAhead;
  const years = Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => first + i);
  if (parts.y && !years.includes(Number(parts.y))) years.push(Number(parts.y));
  const monthName = new Intl.DateTimeFormat(fmt.locale, { month: 'long', timeZone: 'UTC' });
  const dayCount = parts.m ? daysIn(Number(parts.y) || 2024, Number(parts.m)) : 31;

  const change = (patch: Partial<DateParts>) => {
    const next = { ...parts, ...patch };
    if (!next.d || !next.m || !next.y) {
      if (clearable && Object.values(patch).includes('')) {
        setDraft(null);
        if (value) onValueChange('');
      } else setDraft(next);
      return;
    }
    const day = Math.min(Number(next.d), daysIn(Number(next.y), Number(next.m)));
    let iso = `${next.y}-${next.m.padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (min && iso < min) iso = min;
    if (max && iso > max) iso = max;
    setDraft(null);
    if (iso !== value) onValueChange(iso);
  };

  const id = ctx?.id;
  const label = rest['aria-label'];
  const part = (name: string) => (label ? `${label}: ${name}` : name);
  return (
    <div role="group" aria-label={label} className={cn('grid grid-cols-[4.75rem_minmax(0,1fr)_5.75rem] gap-2', className)}>
      <Select
        id={id}
        aria-label={id ? undefined : part(t('date.day'))}
        value={parts.d}
        onChange={(e) => change({ d: e.target.value })}
        disabled={disabled}
        placeholder={t('date.day')}
        required={!clearable}
      >
        {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>
            {fmt.digits(String(d))}
          </option>
        ))}
      </Select>
      <Select
        id={id && `${id}-month`}
        aria-label={part(t('date.month'))}
        value={parts.m}
        onChange={(e) => change({ m: e.target.value })}
        disabled={disabled}
        placeholder={t('date.month')}
        required={!clearable}
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <option key={m} value={m}>
            {monthName.format(Date.UTC(2024, m - 1, 1))}
          </option>
        ))}
      </Select>
      <Select
        id={id && `${id}-year`}
        aria-label={part(t('date.year'))}
        value={parts.y}
        onChange={(e) => change({ y: e.target.value })}
        disabled={disabled}
        placeholder={t('date.year')}
        required={!clearable}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {fmt.digits(String(y))}
          </option>
        ))}
      </Select>
    </div>
  );
}

// ------------------------------------------------------------------ Money

export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'size'> {
  value: string;
  onValueChange: (text: string) => void;
  currency?: Currency;
  size?: 'md' | 'lg';
}

/**
 * Amount field: LTR digits (Arabic-Indic accepted), aligned to the reading start in RTL,
 * with the currency symbol at the end. Parse with parseMoneyInput.
 */
export function MoneyInput({ value, onValueChange, currency, size = 'md', className, ...props }: MoneyInputProps) {
  const fmt = useFormat();
  const field = useFieldProps(props);
  const cur = currency ?? fmt.currency;
  const symbol = currencySymbol(cur, fmt.lang);
  // When a separator was typed, show how the amount was read (in words in
  // Arabic, which no grouping style can make ambiguous).
  const parsed = hasSeparator(value) ? parseMoneyInput(value, cur, { allowZero: true }) : null;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <div
        className={cn(
          controlClass,
          'flex items-center focus-within:border-accent focus-within:outline-2 focus-within:outline-accent',
          size === 'lg' ? 'h-12' : 'h-11',
          field['aria-invalid'] && 'border-danger',
          props.disabled && 'bg-track',
        )}
      >
        <input
          {...props}
          {...field}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          dir="ltr"
          inputMode="decimal"
          autoComplete="off"
          className={cn(
            'h-full min-w-0 flex-1 bg-transparent px-3 outline-none rtl:text-end disabled:cursor-not-allowed',
            size === 'lg' ? 'text-lg font-semibold' : 'text-md font-medium',
          )}
        />
        <span aria-hidden className="shrink-0 pe-3 text-sm text-subtle">
          {symbol}
        </span>
      </div>
      {parsed?.value ? (
        <p className="text-xs text-subtle" aria-live="polite">
          = {fmt.lang === 'ar' ? amountInArabicWords(parsed.value, cur) : fmt.money(parsed.value, { currency: cur })}
        </p>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ Checkbox / Switch

export function Checkbox({ label, hint, className, ...props }: Omit<InputProps, 'type'> & { label: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <label htmlFor={props.id ?? id} className={cn('flex min-h-11 cursor-pointer items-start gap-3 py-2.5', className)}>
      <input id={id} type="checkbox" {...props} className="mt-0.5 size-[18px] shrink-0 cursor-pointer accent-accent" />
      <span className="flex flex-col gap-0.5">
        <span className="text-base">{label}</span>
        {hint ? <span className="text-sm text-subtle">{hint}</span> : null}
      </span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  busy?: boolean;
}

/** A 44px-tall hit area around a 42×24 track. */
export function Switch({ checked, onChange, label, disabled, busy }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className="group grid h-11 w-14 shrink-0 place-items-center rounded-md disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={cn('flex h-6 w-[42px] items-center rounded-full p-[3px] transition-colors', checked ? 'bg-accent' : 'bg-field-line')}>
        <span className={cn('size-[18px] rounded-full bg-white transition-transform', checked ? 'translate-x-[18px] rtl:-translate-x-[18px]' : '')} />
      </span>
    </button>
  );
}

// ------------------------------------------------------------------ Search

export interface SearchInputProps extends Omit<InputProps, 'onChange' | 'value' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
}

export function SearchInput({ value, onValueChange, className, ...props }: SearchInputProps) {
  const t = useT();
  return (
    <div className={cn('relative', className)}>
      <Search aria-hidden className="pointer-events-none absolute start-3 top-1/2 size-[18px] -translate-y-1/2 text-subtle" />
      <input
        type="search"
        {...props}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(controlClass, 'h-11 bg-surface ps-10 pe-11 [&::-webkit-search-cancel-button]:hidden')}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onValueChange('')}
          aria-label={t('common.clearFilters')}
          className="absolute end-0 top-0 grid size-11 place-items-center text-subtle hover:text-ink"
        >
          <X aria-hidden className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

/** Two-column responsive grid for forms. */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}>{children}</div>;
}
